import { AdvertJson, ExternalBuildAdOptions } from '@mbrtargeting/metatag-shared-types/ad-engine';
import { AdvertisingApi, InfotoolAPI, MetaTagCoreInterface, PublisherAPI, SystemEvent, WrapperApi } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { processAndReplaceQueue, waitForDocumentReady } from '@mbrtargeting/metatag-utils';
import { Config } from '../../interfaces/interfaces.js';
import { getAllModuleRegistrationMetadata } from '../decorators/on-module-registration.js';
import { getAllSystemEventMetadata } from '../decorators/on-system-event.js';
import { InjectionToken } from '../di/injection-token.js';
import { Registry } from '../di/registry.js';
import { CONFIG_RESOLVER, METATAG } from '../token.js';
import { ConfigResolver } from './config-resolver.js';
import { selectFullConfig } from './config-selector.js';
import { triggerEvent, waitForEvent } from './events.js';
import { logger } from './logger.js';
import { createQueuePublisherAPI } from './publisher-api.js';

const log = logger.logGroup({ prefix: 'System' });

export class MetaTag implements MetaTagCoreInterface {

    public get version() {
        return __METATAG_VERSION__;
    }

    public registry?: typeof Registry;
    /** @inheritdoc */
    public Publisher: PublisherAPI;
    /** @inheritdoc */
    public Advertising!: AdvertisingApi;
    /** @inheritdoc */
    public Infotool!: InfotoolAPI;
    /** @inheritdoc */
    public Wrapper!: WrapperApi;

    /** @inheritdoc */
    public buildAd!: (jsonData: AdvertJson, options: ExternalBuildAdOptions) => void;

    #isInitialized: boolean = false;
    #deferred: Function[] = [];

    constructor(public cmd: Function[]) {
        if (window.Cypress) {
            this.registry = Registry;
        }
        Registry.register(METATAG, this);
        Registry.register(CONFIG_RESOLVER, new ConfigResolver({} as Config));
        this.Publisher = createQueuePublisherAPI(cmd);
        waitForEvent(SystemEvent.SDG_CORE_FILE_LOADED, () => {
            processAndReplaceQueue(cmd);
        });
        waitForEvent(SystemEvent.SDG_CONFIG_FILE_AVAILABLE, () => {
            processAndReplaceQueue(this.#deferred);
        });
        waitForDocumentReady(document, () => {
            triggerEvent(SystemEvent.SDG_DOM_CONTENT_LOADED, {});
        });
    }

    /** @inheritdoc */
    public configure(config: Partial<Config>): void {
        if (this.#isInitialized) return log.warn('SDG.configure was called after initialization and will be ignored.');
        Registry.lookup(CONFIG_RESOLVER).update(selectFullConfig(), () => config);
    }

    /**
     * Registers a metatag module.
     *
     * @param moduleName Alias for the module
     * @param Class The class with the module implementation.
     */
    public registerModule<Class extends Object>(moduleName: InjectionToken<any>, Class: { new(...args: any[]): Class }): void {
        const invoke = (obj: any, methodName: string): void => {
            try {
                obj[methodName]();
            } catch (e) {
                log.fatal('unhandled error invoking %s on %o', [methodName, obj]);
                throw e;
            }
        };
        this.#deferred.push(() => {
            log.debug('Registering module %s', [moduleName.toString()]);

            const instance = new Class();

            // process all `@onModuleRegistration` decorators
            for (const { methodName } of getAllModuleRegistrationMetadata(instance)) {
                invoke(instance, methodName);
            }

            // process all `@onSystemEvent` decorators and register event listeners
            for (const [methodName, metadataArray] of Object.entries(getAllSystemEventMetadata(instance) || {})) {
                for (const { eventName, options } of metadataArray) {
                    waitForEvent(eventName, () => {
                        invoke(instance, methodName);
                    }, options);
                }
            }

            Registry.register(moduleName, instance);
        });
    }

    /** @inheritdoc */
    public init(): void {
        if (this.#isInitialized) return log.warn('Already initialized.');
        this.#isInitialized = true;
        Registry.lookup(CONFIG_RESOLVER).evaluateSettingSwitches();
        triggerEvent(SystemEvent.SDG_CONFIG_FILE_AVAILABLE, {});
    }
}
